/* eslint-disable no-use-before-define,eqeqeq */
import { define, Status, ajax, fmt, isEmpty, isNumeric, getUserParm, setUserParm, getUserId } from '../twd/twd'

export const Errors = {
    UNEXPECTED: "Currently not available. Please try again later",
    NO_CUSIP: "No data found for this CUSIP",
    NO_LAST_TRADE: "There have been no trades within the past 180 days for this security.",
    NO_TRADE_DATA: "There were no reported trades during the date range specified.",
}

export const controller = define("app.trade-history", {
    async refresh(ctx) {
        const {setStatus, status, cusip, setReloadStatus, setError} = ctx

        const isReload = status!==Status.LOADING && this.issueData && this.issueData.cusip === cusip
        const statusFn = isReload ? setReloadStatus : setStatus
        statusFn(Status.LOADING)
        setError(null)

        setTimeout(async () => {
            try {
                const err = (await this.refreshIssueData(ctx))
                    || (await this.refreshTradeData(ctx))

                if (err) { console.log('trade-history error', err) }

                statusFn(Status.LOADED)
            } catch(e) {
                console.log('trade-history error', e)
                setStatus(Status.FAILED_TO_LOAD)
            }
        }, 1000)
    },

    async refreshIssueData({cusip, setError, setIsDisclaimer}) {
        if (this.issueData && this.issueData.cusip === cusip) return

        this.issueData = {}
        this.fromDate = ''
        this.tradeData = []
        this.lastTradeData = null

        const {data:issueData} = await ajax({
            url:'/esb/portfolio/cusip/issuedata',
            method: 'GET',
            params: { cusip }
        })

        const data_ = this.issueData = (await issueData).IssueData || {}

        if (Object.keys(data_).length===0) {
            setError(Errors.NO_CUSIP)
            return Errors.NO_CUSIP
        }

        if (['A','C'].includes(data_.type) && !getUserParm('HISTORY-DISCLAIMER')) {
            setIsDisclaimer(true)
        }
    },

    attestDisclaimer({setIsDisclaimer}) {
        const a = fmt.mdy(new Date())
        setUserParm('HISTORY-DISCLAIMER', a)
        ajax({
            url: `/esb/administration/userservice/historyDisclaimer?userId=${getUserId()}&value=${a}`,
            method: 'POST',
            params: { }
        })
        setIsDisclaimer(false)
    },

    async refreshTradeData({cusip, filterByDate, setError}) {

        const fromDate = String(filterByDate)
        if (!isEmpty(this.tradeData) && !isEmpty(this.lastTradeData) && this.cusip === cusip && this.fromDate && this.fromDate === fromDate) {
            return
        }

        this.fromDate = fromDate
        this.tradeData = []
        this.lastTradeData = null

        try {
            const {data:tradedata} = await ajax({
                url: '/esb/portfolio/tradehistory',
                method: 'POST',
                params: { cusip, fromDate }
            })

            const { last, details } = (await tradedata) || {}

            if (isEmpty(last)) {
                setError(Errors.NO_LAST_TRADE)
                return Errors.NO_LAST_TRADE
            }

            const hasTradeYield = this.hasTradeYield

            this.lastTradeData = this.adjustTradeType(last, hasTradeYield)

            const detailsByDate = Array.from(details)
                .map((r) => this.adjustTradeType(r, hasTradeYield))
                .map((r) => ({
                    tradeDateTimeValue: new Date(r.tradeDateTime).getTime(),
                    tradeDate: fmt.mdy(r.tradeDateTime),
                    ...r,
                }) )
                .map((r) => ({
                    tradeDateValue: new Date(r.tradeDate).getTime(),
                    ...r,
                }) )
                .filter(({tradeDateValue}) => tradeDateValue)
                .sort(({tradeDateTimeValue:a},{tradeDateTimeValue:b}) => b - a)
                .reduce((x, r) => {
                    const { tradeDateValue:id, tradeDate } = r
                    if (!(id in x)) {
                        x[id] = { tradeDate, tradeDateValue:id, details: [] }
                    }
                    x[id].details.push(r)
                    return x
                }, {})

            this.tradeData = Array.from(Object.entries(detailsByDate))
                .sort((tradeDateValue1,tradeDateValue2) => tradeDateValue2[0] - tradeDateValue1[0])
                .map(([_, a]) => a)
                .map(r => {
                    return {
                        tradeDateValue: r.tradeDateValue,
                        tradeDate: r.tradeDate,
                        totalTrades: r.details.length,
                        hasEstimates: r.details.some(a => a.isEstimate),
                        details: r.details,
                        ...(r.details.reduce((s, a) => {
                            if (!('highPrice' in s) || s.highPrice < a.tradePrice) {
                                s = {...s, highPrice:a.tradePrice, highYield:a.tradeYield, highQuantity:a.tradeQuantity, highIsEstimate:a.isEstimate }
                            }
                            if (!('lowPrice' in s) || s.lowPrice > a.tradePrice) {
                                s = {...s, lowPrice:a.tradePrice, lowYield:a.tradeYield, lowQuantity:a.tradeQuantity, lowIsEstimate:a.isEstimate }
                            }
                            return s
                        }, {})),
                    }
                })

            if (this.tradeData.length === 0) {
                setError(Errors.NO_TRADE_DATA)
                return Errors.NO_TRADE_DATA
            }
        } catch(e) {
            console.log('trade-history error', e)
            setError(Errors.NO_TRADE_DATA)
            return Errors.NO_TRADE_DATA
        }
    },

    tradeTypesMapping: {
        "SALES": "Customer Buy",
        "PURCHASE": "Customer Sell",
        "DEALER": "Dealer to Dealer",
        "SALES.HISTORY": "Customer Buy",
        "PURCHASE.HISTORY": "Customer Sell",
        "DEALER.HISTORY": "Dealer to Dealer",
    },

    adjustTradeType(r, hasTradeYield) {
        const a = r.tradeType
        return {
            ...r,
            tradeType:this.tradeTypesMapping[a] || a,
            isEstimate: r.estimateFlag==='Y',
            tradeYield: hasTradeYield ? r.tradeYield : null,
        }
    },

    get cusip() {
        return this.issueData.cusip
    },

    get issuer() {
        return this.issueData.issueDisplay
    },

    get lastTrade() {
        return this.lastTradeData || {}
    },

    get highestTrade() {
        return this.tradeData.reduce((r, t) => {
            return (!r || t.highPrice > r.tradePrice)
                ? {...t, tradePrice:t.highPrice, tradeYield:t.highYield }
                : r
        }, null)
    },

    get lowestTrade() {
        return this.tradeData.reduce((r, t) => {
            return (!r || t.lowPrice < r.tradePrice)
                ? {...t, tradePrice:t.lowPrice, tradeYield:t.lowYield }
                : r
        }, null)
    },

    get trades() {
        return this.tradeData || []
    },

    get tradeDates() {
        return this.trades.map(trade => trade.tradeDate)
    },

    get lastTradeDate() {
        return (this.trades[0] || {}).tradeDate
    },

    get hasEstimates() {
        return this.trades.some(trade => trade.hasEstimates)
    },

    get tradeDetails() {
        const details = this.trades.flatMap(trade => trade.details)
        const prices = details.map(a => a.tradePrice)
        const yields = details.map(a => a.tradeYield).filter(a => isNumeric(a))
        const quantities = details.map(a => a.tradeQuantity)
        const n = details.length
        return {
            details,
            summary: {
                totalTrades: n,
                averagePrice: prices.reduce((acc, a) => acc + a, 0) / n,
                averageYield: yields.length > 0 ? yields.reduce((acc, a) => acc + a, 0) / yields.length : null,
                averageQuantity: quantities.reduce((acc, a) => acc + a, 0) / n,
                hasEstimates: details.some(a => a.isEstimate),
            }
        }
    },


    customerBuys({showDataAs}) {
        return this.chartData("Customer Buy", this.getValueField(showDataAs))
    },

    customerSells({showDataAs}) {
        return this.chartData("Customer Sell", this.getValueField(showDataAs))
    },

    dealerToDealers({showDataAs}) {
        return this.chartData("Dealer to Dealer", this.getValueField(showDataAs))
    },

    getValueField: (showDataAs) => {
        return showDataAs === 'price-history' ? 'tradePrice' : 'tradeYield'
    },


    get hasTradeYield() {
        const { type, category, couponTypeCode } = this.issueData || {}
        const a = ['P','O','R','K'].includes(type)
            || (['C','S'].includes(type) && category==='N')
            || ['E','0'].includes(couponTypeCode)
        return !a
    },

    chartData(tradeType, valueField) {
        return this.trades
            .map(trade => {
                return trade.details
                    .filter(a => a.tradeType === tradeType)
                    .map(a => ({
                        x: a.tradeDateTimeValue,
                        y: a[valueField],
                        ...a,
                    }))
            })
            .flat()
            .sort((a, b) => a.x - b.x)
    },

    chartYDomain({showDataAs}, {showCustomerBuys, showCustomerSells, showDealerToDealers}) {
        const rs = []
        if (showCustomerBuys) {
            rs.push(...(this.customerBuys({showDataAs}) || []).map(a => a.y).filter(y => y != null))
        }
        if (showCustomerSells) {
            rs.push(...(this.customerSells({showDataAs}) || []).map(a => a.y).filter(y => y != null))
        }
        if (showDealerToDealers) {
            rs.push(...(this.dealerToDealers({showDataAs}) || []).map(a => a.y).filter(y => y != null))
        }

        if (rs.length === 0) {
            return [0, 1]
        }

        const min = Math.min(...rs)
        const max = Math.max(...rs)
        const range = max - min
        const padding = range > 0 ? range * 0.1 : Math.max(Math.abs(min) * 0.1, Math.abs(max) * 0.1, 1)
        return [min - padding, max + padding]
    },

    chartXDomain({showDataAs, filterByDate}, {showCustomerBuys, showCustomerSells, showDealerToDealers}) {
        const rs = []
        if (showCustomerBuys) {
            rs.push(...(this.customerBuys({showDataAs}) || []).map(a => a.x).filter(x => x != null))
        }
        if (showCustomerSells) {
            rs.push(...(this.customerSells({showDataAs}) || []).map(a => a.x).filter(x => x != null))
        }
        if (showDealerToDealers) {
            rs.push(...(this.dealerToDealers({showDataAs}) || []).map(a => a.x).filter(x => x != null))
        }

        const now = new Date()
        const from = new Date(now)
        from.setDate(from.getDate() - parseInt(filterByDate, 10))
        return [from.getTime(), now.getTime()]
    },

    chartXTicks(_, [minx, maxx], maxTicks = 9) {
        const dayMs = 24 * 60 * 60 * 1000
        const ts = []
        let t = minx
        while (t <= maxx) {
            ts.push(t)
            t += dayMs
        }

        if (ts.length > maxTicks) {
            const step = Math.ceil(ts.length / maxTicks)
            return ts.filter((_, i) => i % step === 0)
        }
        return ts
    },
})



// WEBPACK FOOTER //
// ./src/trade-history/controller.js